import { Add } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { GreenText, SortableTableCell } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleExporterNoteDto } from 'dtos'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { VehicleExporterNoteParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { formatDateTime, useDebounce } from 'utils'
import ExporterNoteAddEditDialog from './ExporterNoteAddEditDialog'

interface ExporterNotesListCardProps {
  vehicle: VehicleDto
}

export default function ExporterNotesListCard({ vehicle }: ExporterNotesListCardProps) {
  const [isGettingExporterNotes, setIsGettingExporterNotes] = useState<boolean>(false)
  const [vehicleExporterNotes, setVehicleExporterNotes] = useState<VehicleExporterNoteDto[]>([])
  const [exporterNote, setExporterNote] = useState<VehicleExporterNoteDto>(new VehicleExporterNoteDto())
  const [ExporterNoteAddEditDialogOpen, setExporterNoteAddEditDialogOpen] = useState<boolean>(false)
  const [isExporterNoteSaving, setIsExporterNoteSaving] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)

  const [parameters, setParameters] = useState<VehicleExporterNoteParameters>({
    page: 0,
    pageSize: 5,
    orderBy: 'createdDateTime',
    order: 'desc'
  })

  const onChangeOrderBy = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      getVehicleExporterNotes(vehicle.vehicleGuid, parameters)
    }
  }, [vehicle.vehicleGuid, parameters])

  const getVehicleExporterNotes = useDebounce((vehicleGuid: string, parameters: VehicleExporterNoteParameters) => {
    setIsGettingExporterNotes(true)
    controllers
      .getVehicleExporterNotes(vehicleGuid, parameters)
      .then(response => {
        setVehicleExporterNotes(response.value)
        setTotalCount(response.totalCount!)
      })
      .finally(() => {
        setIsGettingExporterNotes(false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <>
      <ExporterNoteAddEditDialog
        initialValues={exporterNote}
        isLoading={isExporterNoteSaving}
        onClose={() => {
          setExporterNoteAddEditDialogOpen(false)
        }}
        onSave={values => {
          setIsExporterNoteSaving(true)
          controllers
            .createExporterNote(values, vehicle.vehicleGuid!)
            .then(response => {
              enqueueSnackbar('Exporter Note Saved Successfully!', { variant: 'success' })
              const exporterNote = response.value
              setExporterNote(exporterNote)
              getVehicleExporterNotes(vehicle.vehicleGuid, parameters)
              setExporterNoteAddEditDialogOpen(false)
            })
            .finally(() => {
              setIsExporterNoteSaving(false)
            })
        }}
        open={ExporterNoteAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={8}>
                <Typography fontWeight='bold' variant='h5'>
                  Exporter Notes
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Add />}
                  onClick={() => {
                    setExporterNote(new VehicleExporterNoteDto())
                    setExporterNoteAddEditDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD
                </Button>
              </Grid>

              {isGettingExporterNotes && (
                <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                  <Grid>
                    <CircularProgress color='primary' size={50} />
                  </Grid>
                </Grid>
              )}

              {!isGettingExporterNotes && (
                <Grid item xs={12}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <SortableTableCell
                            onChangeSortByDirection={onChangeOrderBy}
                            propertyName='createdDateTime'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Time Stamp
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeOrderBy}
                            propertyName='createdBy'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Entered By
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeOrderBy}
                            propertyName='note'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Note
                          </SortableTableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {vehicleExporterNotes.map(note => (
                          <TableRow>
                            <TableCell>{note.createdDateTime ? formatDateTime(note.createdDateTime) : ''}</TableCell>

                            <TableCell>
                              {note.createdBy?.firstName} {note.createdBy?.lastName}
                            </TableCell>

                            <TableCell>{note.note}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>

                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            count={totalCount}
                            onPageChange={(_, page) => {
                              setParameters({ ...parameters, page })
                            }}
                            onRowsPerPageChange={e => {
                              setParameters({ ...parameters, page: 0, pageSize: e.target.value as unknown as number })
                            }}
                            page={parameters.page}
                            rowsPerPage={parameters.pageSize}
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            SelectProps={{ fullWidth: false }}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`**Exporter Notes:**

Notes are listed in ascending order by Time Stamp. 

Add+ opens a dialog for the user to enter a note. Note Date and Entered by is automatically populated based on the current time and
user signed in.

---`}
                </GreenText>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
